import { type FC, type KeyboardEventHandler } from 'react';
import { Box, Flex, Grid, GridItem } from '@chakra-ui/react';
import parse from 'html-react-parser';
import { FormattedMessage } from 'react-intl';
import { ShippingOptionType } from '../../../bff-api/baskets/model';
import { useBasket } from '../../../bff-api/baskets/useBasket';
import { usePaymentMethods } from '../../../bff-api/baskets/usePaymentMethods';
import { isNotAvailableStatus } from '../../../bff-api/utils/store-utils';
import { AddToBagButton } from '../../../components/add-to-bag-section/add-to-bag-button/AddToBagButton';
import { AfterpaySection } from '../../../components/afterpay-section';
import { ApplePayButton } from '../../../components/apple-pay-button';
import { ClientOnly } from '../../../components/client-only/ClientOnly';
import { ExtendWarranty } from '../../../components/extend-warranty';
import { useExtendWarranty } from '../../../components/extend-warranty/useExtendWarranty';
import { GetIcon } from '../../../components/icons';
import { PfaWarning } from '../../../components/pfa-warning';
import { ProductPrice } from '../../../components/product-price';
import { PromotionBadges } from '../../../components/promotion-badges';
import { WishlistButton } from '../../../components/wishlist/WishlistButton';
import { DEFAULT_CURRENCY } from '../../../constants';
import { usePDP } from '../../../contexts/pdp/usePDP';
import { useTurnToComponentData } from '../../../contexts/useTurnToComponentData';
import { useNavigateTo } from '../../../hooks';
import { useAddProductToBag } from '../../../hooks/useAddProductToBag';
import { useBuildURL } from '../../../hooks/useBuildURL';
import { Button, Card, Header, Rating, Text } from '../../../ui-components';
import { useUrl } from '../../../utils/api';
import { isOutOfStock } from '../../../utils/product-utils';
import { isHydrated } from '../../../utils/utils';
import { PageUrl } from '../../utils/page-url';
import { PDPDeliveryOptions } from '../delivery-options';
import { BrandShopLink } from './brand-shop-link/BrandShopLink';
import { HandlingFee } from './handling-fee';
import { IsSaleFinal } from './is-sale-final/IsSaleFinal';
import { ProductPerks } from './product-perks';
import { QuantityBox } from './quantity-box';
import { BuyBoxVariants } from './variants/BuyBoxVariants';

interface Props {
    headerHeight: number;
}

export const PDPBuyBox: FC<Props> = ({ headerHeight }: Props) => {
    const { data: basket } = useBasket();
    const {
        product,
        inventoryStore: {
            currentInventory,
            flags: { hasEnoughStock },
        },
        selectedShippingOptionType,
        selectedQuantity,
        isLoadingCurrentInventory,
        getElementRef,
    } = usePDP();
    const extend = useExtendWarranty();
    const paymentMethods = usePaymentMethods({ productId: product?.id }).data;
    const giftRegistryUrl = useUrl(PageUrl.ADD_TO_REGISTRY, [
        'pid',
        product?.id ?? '',
        'source',
        'productdetail',
        'Quantity',
        selectedQuantity.toString(),
    ]);
    const { reviewsRef, onToggleWriteReviewModal } = useTurnToComponentData();
    const addProductToBag = useAddProductToBag({ openDrawer: true });
    const navigateTo = useNavigateTo();
    const { buildPdpURL } = useBuildURL();

    if (!product) {
        return null;
    }

    const onAddProductToGiftRegistry = (): void => {
        navigateTo(giftRegistryUrl);
    };

    const onSeeSimilarItems = (): void => {
        const headerRef = getElementRef('layout-header').current;
        const recommendationsRef = getElementRef('recommendations-container').current;

        const to =
            window.scrollY +
            (recommendationsRef?.getBoundingClientRect()?.y ?? 0) -
            (headerRef?.getBoundingClientRect()?.height ?? 0);

        window.scrollTo({ top: to, behavior: 'smooth' });
    };

    const scrollToReviews = (): void => {
        if (reviewsRef) reviewsRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const {
        name = '',
        currency = DEFAULT_CURRENCY,

        c_dropShipEligibility: isDropShip,
        c_additionalShippingCharges: handlingFee,
        isSaleFinal,
        c_brandShopUrl,
        brand,
        c_promotions = [],
        isApplePayElegible,
    } = product;

    const shouldShowBrandShopLink = !!brand && !!c_brandShopUrl;
    const outOfStock = isOutOfStock(product);
    const isNotAvailable = isNotAvailableStatus(currentInventory.status);

    const isAddToBagDisabled =
        !basket ||
        outOfStock ||
        (!addProductToBag.isLoading && (!hasEnoughStock || isLoadingCurrentInventory));
    const isAddToBagLoading = addProductToBag.isLoading || isLoadingCurrentInventory;

    const onKeyDownQuantity: KeyboardEventHandler<HTMLInputElement> = (e): void => {
        if (e.key === 'Enter' && !isAddToBagDisabled && !isAddToBagLoading) {
            addProductToBag.mutate({
                product,
                quantity: selectedQuantity,
                shippingOptionType: selectedShippingOptionType,
                applyExtend: true,
                store: currentInventory.store,
            });
        }
    };

    const extendPlan = extend.getPlan();
    const params = {
        pid: product?.id || '',
        ...(extendPlan
            ? {
                  extendPlanId: extendPlan.planId,
                  extendPrice: extendPlan.price,
                  extendTerm: extendPlan.term,
              }
            : {}),
        isFromPWA: 'true',
        isProductVariant: 'true',
        storeSelectedId: currentInventory?.store?.id || '',
        eddCheck: String(selectedShippingOptionType === ShippingOptionType.SHIP_TO),
        shipOption:
            selectedShippingOptionType === ShippingOptionType.SHIP_TO
                ? 'shipToAddress'
                : 'shipToStore',
        Quantity: selectedQuantity ?? 1,
        source: 'pdp',
    };

    return (
        <Card id="buy-box-card">
            <Flex direction="column" position="relative">
                <Flex data-testid="buy-box-product-information" direction="column">
                    <Grid templateColumns="1fr auto" gap={4}>
                        <GridItem>
                            {shouldShowBrandShopLink ? (
                                <div className="flex justify-start">
                                    <BrandShopLink
                                        brandName={brand}
                                        brandShopUrl={c_brandShopUrl}
                                    />
                                </div>
                            ) : null}

                            <Box marginTop={shouldShowBrandShopLink ? 1 : 0}>
                                <Header
                                    data-testid="buy-box-product-name"
                                    as="h1"
                                    style="bold"
                                    color="slt-pepper.1000"
                                    variant="header4"
                                >
                                    {parse(name)}
                                </Header>
                            </Box>
                        </GridItem>
                        <GridItem rowSpan={2}>
                            <WishlistButton productId={product.id} variant="circle" size="md" />
                        </GridItem>
                    </Grid>

                    <Flex
                        className="space-x-2"
                        onClick={() =>
                            product?.c_turntoTotalReviewCount === 0
                                ? onToggleWriteReviewModal(true)
                                : scrollToReviews()
                        }
                        color="slt-pepper.1000"
                        alignItems="center"
                        cursor="pointer"
                    >
                        {typeof product?.c_turntoAverageRating === 'string' ? (
                            <Rating
                                initialRating={Number(product?.c_turntoAverageRating)}
                                disabled
                                iconSize="sm"
                            />
                        ) : null}

                        {product?.c_turntoTotalReviewCount === 0 ? (
                            <Button
                                variant="text-button-no-hover"
                                textSize="small"
                                textStyle="regular"
                            >
                                <FormattedMessage
                                    defaultMessage="Write a review"
                                    id="buy-box-reviews-write"
                                />
                            </Button>
                        ) : (
                            <Text size="small">
                                <FormattedMessage
                                    defaultMessage="({reviewsNumber}) Reviews"
                                    id="buy-box-reviews-total"
                                    values={{ reviewsNumber: product?.c_turntoTotalReviewCount }}
                                />
                            </Text>
                        )}
                    </Flex>
                    <Box marginTop={6}>
                        <Header as="header" id="buy-box-price" variant="header4">
                            <ProductPrice
                                id="buy-box"
                                prices={product.prices}
                                price={product.price}
                                currency={product.currency}
                            />

                            {isDropShip && !!handlingFee ? (
                                <HandlingFee handlingFee={handlingFee} currency={currency} />
                            ) : null}
                        </Header>
                    </Box>

                    <PromotionBadges id="buy-box-promotion-badges" promotions={c_promotions} />
                </Flex>

                <BuyBoxVariants
                    product={product}
                    onChange={(selectedVariant) => {
                        if (product.id !== selectedVariant) {
                            navigateTo(buildPdpURL(product, selectedVariant));
                        }
                    }}
                    className="mt-6"
                />

                {!outOfStock ? (
                    <QuantityBox className="mt-6" onKeyDown={onKeyDownQuantity} />
                ) : null}

                <PDPDeliveryOptions id="buy-box-shipping-options" className="mt-6" />

                <ExtendWarranty productId={product.id} className="mt-8" />

                <Flex data-testid="buy-box-actions" direction="column" className="mt-8">
                    {currentInventory?.store?.stateCode &&
                    (selectedShippingOptionType === ShippingOptionType.SDD ||
                        selectedShippingOptionType === ShippingOptionType.BOPIS) ? (
                        <PfaWarning
                            id="pfa-pdp-message"
                            alignItems="center"
                            containsPfa={product?.c_containsPfa}
                            stateCode={currentInventory?.store?.stateCode}
                        />
                    ) : null}
                    {selectedShippingOptionType === ShippingOptionType.SHIP_TO ? (
                        <PfaWarning
                            id="pfa-pdp-message"
                            alignItems="center"
                            containsPfa={product?.c_containsPfa}
                        />
                    ) : null}

                    {isSaleFinal ? (
                        <Box marginBottom={2}>
                            <IsSaleFinal />
                        </Box>
                    ) : null}
                    <Flex className="space-y-4" direction="column">
                        {isApplePayElegible &&
                        !outOfStock &&
                        !isAddToBagDisabled &&
                        !isNotAvailable &&
                        selectedShippingOptionType !== ShippingOptionType.SDD &&
                        isHydrated() ? (
                            <ApplePayButton
                                step="newpdp"
                                basket={basket}
                                product={product}
                                params={params}
                            />
                        ) : null}

                        <AddToBagButton trackIfInViewport headerHeight={headerHeight} />

                        <ClientOnly>
                            <ProductPerks />
                        </ClientOnly>

                        {outOfStock ? (
                            <Button
                                data-testid="buy-box-see-similar-items"
                                onClick={onSeeSimilarItems}
                                variant="tertiary"
                            >
                                <FormattedMessage
                                    id="buy.box.see.similar.items"
                                    defaultMessage="See similar Items"
                                />
                            </Button>
                        ) : (
                            <>
                                <Button
                                    data-testid="buy-box-add-to-registry"
                                    leftIcon={<GetIcon name="add-to-registry" size="md" />}
                                    onClick={onAddProductToGiftRegistry}
                                    variant="tertiary"
                                >
                                    <FormattedMessage
                                        id="buy.box.add.to.registry"
                                        defaultMessage="Add to Registry"
                                    />
                                </Button>

                                {paymentMethods?.afterPay.enabled ? (
                                    <AfterpaySection
                                        product={product}
                                        afterpayConfig={paymentMethods.afterPay.config}
                                        eligible={paymentMethods.afterPay.eligible}
                                        mode="messaging"
                                    />
                                ) : null}
                            </>
                        )}
                    </Flex>
                </Flex>
            </Flex>
        </Card>
    );
};
